import { waitForEvent } from "@kluntje/js-utils/lib/dom-helpers";

const UC_INITIALIZED_EVENT = "UC_UI_INITIALIZED";

/**
 * singleton to be the bridge to Usercentrics consent management and allow accessing user consent information.
 */
class ConsentManagementService {
  async whenUCReady(): Promise<void> {
    // already loaded
    if ("UC_UI" in window) return;

    await waitForEvent(window, UC_INITIALIZED_EVENT);
  }

  async getUCServices() {
    await this.whenUCReady();
    return window.UC_UI!.getServicesBaseInfo();
  }

  /**
   * user has already given consent to the given service in the ucercentric cookie consent layer
   *
   * @param {string} serviceName - name of the service e.g. "Sherpa" not the ID (e.g. "nBWG7zxWG")
   * @returns {Promise<boolean>} - true if the user has set given their consent
   */
  async hasConsent(serviceName: string): Promise<boolean> {
    const service = await this.getServiceByName(serviceName);

    // no service, no consent
    if (!service) return false;

    return service.consent.status;
  }

  /**
   * set consent for the given service via usercentrics API
   *
   * @param {string} serviceName - name of the service e.g. "Sherpa" not the ID (e.g. "nBWG7zxWG")
   * @async
   */
  async setConsent(serviceName: string): Promise<void> {
    const serviceID = await this.getServiceID(serviceName);

    if (!serviceID) return;

    window.UC_UI!.acceptService(serviceID);
  }

  /**
   * return the Usercentrics ID for a service
   *
   * @param {string} serviceName - name of the service e.g. "Sherpa" not the ID (e.g. "nBWG7zxWG")
   * @returns {Promise<string | undefined>} - ID of the service e.g. "nBWG7zxWG" or `undefined` when no service can be found for the given service name
   */
  async getServiceID(serviceName: string): Promise<string | undefined> {
    const service = await this.getServiceByName(serviceName);

    return service?.id;
  }

  async getServiceByName(serviceName: string) {
    const services = await this.getUCServices();
    const service = services.find(service => service.name === serviceName);

    if (service === undefined) {
      console.error(`Consent Manager Service: can't find any service for the given name: '${serviceName}'`);
    }

    return service;
  }

  /**
   * opens Usercentrics cookie consent layer, and can jump directly to the given service if the name is provided
   *
   * @param {string?} serviceName - name of the service e.g. "Sherpa" not the ID (e.g. "nBWG7zxWG") which will be focused in the layer
   * @async
   */
  async showConsentLayer(serviceName?: string) {
    await this.whenUCReady();
    if (serviceName) {
      const serviceID = await this.getServiceID(serviceName);
      window.UC_UI!.showSecondLayer(serviceID);
    } else {
      window.UC_UI!.showFirstLayer();
    }
  }
}

export default new ConsentManagementService();
